import React, {useEffect, useState} from 'react';
import ReactSelect from 'react-select';

import {OrderHeader} from './OrderHeader';
import {NestedForm} from '../NestedForm';
import {InvoiceAutocompleter} from './InvoiceAutocompleter';
import {Modal} from '../../Modal';
import {ActivityIndicator} from '../../ActivityIndicator';
import {Choice, FieldProp} from '../../../types';

import {
    getFormStateUpdatesForResolution,
    getLinesForSelectedInvoice,
    getSelectedValidationResolution,
    getValidationErrorComponent,
    getWorkflowIdForSalesOrg,
    handleLineNetPriceBlur,
    handleLineNetPriceChange,
    handleLineNetRevenueBlur,
    handleLineNetRevenueChange,
    handleLineQuantityBlur,
    handleLineQuantityChange,
    handleSaveSelectedLines,
    handleLineUnitOfMeasureChange,
    selectInvoiceLine,
    unselectInvoiceLine,
    validateAndSetDefaults,
    handleLinePricingPerChange,
    handleLineNumberChange,
    handleMaterialChange
} from './functions';
import {fieldValueText} from '../../../_helpers/form';

type TorqueInvoiceSearchProps = FieldProp & {};

export type InvoiceLine = {
    id: string;
    salesOrderNumber: string;
    invoiceNumber: string;
    invoiceDate: string;
    materialNumber: string;
    customerNumber: string;
    netRevenue: any;
    netPrice: any;
    lineNumber: any;
    sfdcLineNumber: any;
    quantity: any;
    businessUnit: string;
    unitOfMeasure: string;
    pricingPer: number;
    custom: boolean;
    plant: string;
}
export type Invoice = {
    salesOrderNumber: string;
    invoiceNumber: string;
    customerPoNumber: string;
    customerNumber: string;
    customerName: string;
    customerCity: string;
    customerState: string;
    customerPostalCode: string;
    salesOrg: string;
    shipToCustomerNumber: string;
    shipToCustomerName1: string;
    shipToCustomerAddress: string;
    shipToCustomerCity: string;
    shipToCustomerZip: string;
    shipToCustomerCountry: string;
    shipToCustomerLanguage: string;
    shipToCustomerState: string;
    documentCategory: string;
}
export type SearchModalState = {
    show: boolean;
    selectedInvoice?: Choice & Invoice;
    isSearchingLines: boolean;
    lines: InvoiceLine[];
    errorMessage: string;
    selectedLines: string[];
    validationErrors: string[];
    workingValidationErrorIndex: number;
    validationResolutions: any; // map of update/keep answers to each validation error
    formStateUpdates: any; // updates to be passed to onChange handler to update main form state
    goForSave: boolean; // flag to indicate that the searchModal can be saved
}

const initialSearchModalState: SearchModalState = {
    show: false,
    isSearchingLines: false,
    selectedInvoice: undefined,
    lines: [],
    errorMessage: '',
    selectedLines: [],
    validationErrors: [],
    workingValidationErrorIndex: 0,
    validationResolutions: {},
    formStateUpdates: {},
    goForSave: false
};

const unitOfMeasureOptions = ['EA', '%', '10', 'BAG', 'BOX', 'CCM', 'CDM', 'CM', 'CM2', 'CS', 'DR', 'DZ', 'FT', 'FT2', 'G', 'GLL', 'H', 'HG', 'IN', 'IN2', 'IN3', 'KAN', 'KAR', 'KG', 'KM', 'L', 'LB', 'LE', 'LOT', 'M', 'M2', 'M3', 'MG', 'ML', 'MM', 'MPC', 'OZ', 'PAA', 'PAK', 'PAL', 'PT', 'QT', 'ROL', 'SET', 'SH', 'SKM', 'SMI', 'SPC', 'ST', 'STD', 'TO', 'TOZ', 'YD', 'YD2', 'ZEA', 'ZSH'];
const legacyUnitOfMeasureOptions = ['EA', 'C', 'M', '%', '10', 'BAG', 'BOX', 'CCM', 'CDM', 'CM', 'CM2', 'CS', 'DR', 'DZ', 'FT', 'FT2', 'G', 'GLL', 'H', 'HG', 'IN', 'IN2', 'IN3', 'KAN', 'KAR', 'KG', 'KM', 'L', 'LB', 'LE', 'LOT', 'M', 'M2', 'M3', 'MG', 'ML', 'MM', 'MPC', 'OZ', 'PAA', 'PAK', 'PAL', 'PT', 'QT', 'ROL', 'SET', 'SH', 'SKM', 'SMI', 'SPC', 'ST', 'STD', 'TO', 'TOZ', 'YD', 'YD2', 'ZEA', 'ZSH'];

/**
 * Wrapper around NestedForm which adds a custom modal for looking up Torque invoices by Invoice#
 * @param value
 * @param onChange
 * @param rest
 * @constructor
 */
export const TorqueInvoiceSearch: React.FunctionComponent<TorqueInvoiceSearchProps> = ({
                                                                                           value,
                                                                                           onChange,
                                                                                           form,
                                                                                           formState,
                                                                                           field,
                                                                                           ...rest
                                                                                       }): JSX.Element => {
    const [searchModalState, setSearchModalState] = useState<SearchModalState>({...initialSearchModalState});

    const workflowFieldMap = {
        soldToCustomerNumberFieldId: 2,
        soldToCustomerNameFieldId: 4,
        sapSalesOrderNumberFieldId: 26,
        customerPoNumberFieldId: 27,
        customerPoDateFieldId: 157,
        customerPoTypeFieldId: 158,
        referenceInvoiceNumberFieldId: 156,
        serviceRenderedDateFieldId: 165,
        pricingDateFieldId: 166,
        distributionChannelFieldId: 154,
        divisionFieldId: 155,
        salesOrgFieldId: 1,
        businessLineFieldId: 89,
        hasSapInvoice: 153,
        plantFieldId: 0,

        lineItemInvoiceNumber: 1,
        lineItemLineNumber: 2,
        lineItemMaterialNumber: 3,
        lineItemQuantity: 5,
        lineItemNetPrice: 6,
        lineItemNetRevenue: 7,
        lineItemSfdcLineNumber: 8,
        lineItemUnitOfMeasure: 10,
        lineItemShortText: 11,
        lineDiscount: 0,
        lineDiscountPrice: 0,
        linePricingPer: 0,

        shipToCustomerNumberFieldId: 0,
        shipToCustomerName1FieldId: 0,
        shipToCustomerAddressFieldId: 0,
        shipToCustomerCityFieldId: 0,
        shipToCustomerZipFieldId: 0,
        shipToCustomerCountryFieldId: 0,
        shipToCustomerLanguageFieldId: 0,
        shipToCustomerStateFieldId: 0,

        documentCategoryFieldId: 0,

        ...field?.flowCustomDisplayComponentConfig?.workflowFieldMap
    };

    const invoiceSearchPipeline = field?.flowCustomDisplayComponentConfig?.invoiceSearchPipeline || 'torque-invoice-autocompleter-new';
    const invoiceLineSearchpipeline = field?.flowCustomDisplayComponentConfig?.invoiceLineSearchPipeline || 'torque-invoice-lines-new';
    const invoiceAutocompleterControlTitle = field?.flowCustomDisplayComponentConfig?.invoiceAutocompleterControlTitle || 'Enter a Torque Invoice# below.';

    const requireSfdcLineNumber = field?.flowCustomDisplayComponentConfig?.requireSfdcLineNumber || false;
    const validSalesOrgs = field?.flowCustomDisplayComponentConfig?.validSalesOrgs || [];

    const validUnitOfMeasures = validSalesOrgs.includes('CAS6') ? legacyUnitOfMeasureOptions : unitOfMeasureOptions;
    const customMaterialNumbers = field?.flowCustomDisplayComponentConfig?.customMaterialNumbers || [];

    useEffect(() => {
        if (searchModalState?.selectedInvoice?.value) {
            getLinesForSelectedInvoice(invoiceLineSearchpipeline, searchModalState, setSearchModalState);
        }
    }, [searchModalState.selectedInvoice]);

    useEffect(() => {
        if (searchModalState.goForSave) {
            handleSaveSelectedLines(onChange, value, searchModalState, workflowFieldMap);

            setSearchModalState({
                ...initialSearchModalState
            });
        }
    }, [searchModalState.goForSave]);

    const hasSapInvoice = fieldValueText(formState, form, workflowFieldMap.hasSapInvoice);
    const additionalButtons = [];

    if (hasSapInvoice === 'Yes') {
        additionalButtons.push((
            <button className="btn btn-sm btn-secondary ml-2"
                    onClick={() => {
                        setSearchModalState((state: SearchModalState) => ({
                            ...state,
                            show: true
                        }))
                    }}
            >
                Search by Invoice#
            </button>
        ));

        field.flowCustomDisplayComponentConfig = {
            ...field?.flowCustomDisplayComponentConfig,
            buttons: {
                ...field?.flowCustomDisplayComponentConfig?.buttons,
                add: {
                    ...field?.flowCustomDisplayComponentConfig?.buttons?.add,
                    hide: true
                }
            }
        };

    } else {
        field.flowCustomDisplayComponentConfig = {
            ...field?.flowCustomDisplayComponentConfig,
            buttons: {
                ...field?.flowCustomDisplayComponentConfig?.buttons,
                add: {
                    ...field?.flowCustomDisplayComponentConfig?.buttons?.add,
                    hide: false
                }
            }
        };
    }

    return (
        <>
            <div>
                <NestedForm
                    {...rest}
                    field={field}
                    value={value}
                    onChange={onChange}
                    additionalButtons={additionalButtons}
                    form={form}
                    formState={formState}
                />
            </div>
            {searchModalState.show &&
                <Modal size="xl">
                    <div className="modal-header">
                        <h5 className="modal-title">Add Lines from Invoice</h5>
                        <button type="button"
                                className="close"
                                tabIndex={-1}
                                onClick={() => {
                                    setSearchModalState(() => ({
                                        ...initialSearchModalState
                                    }))
                                }}
                        >×
                        </button>
                    </div>
                    <div className="modal-body torque-invoice-search">

                        <InvoiceAutocompleter
                            pipeline={invoiceSearchPipeline}
                            title={invoiceAutocompleterControlTitle}
                            searchModalState={searchModalState}
                            setSearchModalState={setSearchModalState}
                        />

                        <div className="mt-3 mb-3">
                            {searchModalState.selectedInvoice &&
                                <>
                                    {validSalesOrgs?.length > 0 && !validSalesOrgs.includes(searchModalState?.selectedInvoice?.salesOrg) &&
                                        <div className="row mb-2">
                                            <div className="col-10 offset-1">
                                                <div className="alert alert-danger">
                                                    <span className="align-self-center ml-1 flex-fill flex-grow-1">
                                                        The Sales Org for the current invoice is {searchModalState.selectedInvoice.salesOrg}.
                                                        This workflow is only valid for the Sales Org(s) {validSalesOrgs.join(', ')}.
                                                        {getWorkflowIdForSalesOrg(searchModalState.selectedInvoice.salesOrg) &&
                                                            <>You can enter a request for {searchModalState.selectedInvoice.salesOrg} <a href={`/p/flow/new/${getWorkflowIdForSalesOrg(searchModalState.selectedInvoice.salesOrg)}`}>here</a></>
                                                        }
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    }
                                    <div className="row mb-2">
                                        <OrderHeader invoice={searchModalState.selectedInvoice}/>
                                    </div>
                                </>
                            }

                            {searchModalState.isSearchingLines && <ActivityIndicator/>}

                            {searchModalState.lines.length > 0 &&
                                <table className="table">
                                    <thead>
                                    <tr>
                                        <th>
                                            <input type="checkbox"
                                                   checked={searchModalState.selectedLines.length === searchModalState.lines.length}
                                                   onChange={(e) => {
                                                       e.target.checked ?
                                                           setSearchModalState((state: SearchModalState) => ({
                                                               ...state,
                                                               selectedLines: state.lines.map((line: InvoiceLine) => line.id)
                                                           }))
                                                           :
                                                           setSearchModalState((state: SearchModalState) => ({
                                                               ...state,
                                                               selectedLines: []
                                                           }))
                                                   }}
                                            />
                                        </th>
                                        <th>Line</th>
                                        <th>SFDC Case Line#</th>
                                        <th>Material#</th>
                                        <th>Business Unit</th>
                                        <th>Unit of Measure</th>
                                        <th>Pricing Per</th>
                                        <th>Quantity</th>
                                        <th>Amount</th>
                                        <th>Extended Amount</th>
                                    </tr>
                                    </thead>
                                    <tbody>

                                    {searchModalState.lines.map((line: InvoiceLine) => (
                                        <tr key={line.id}
                                            className={[
                                                searchModalState.selectedLines.includes(line.id) && 'table-warning'
                                            ].filter(Boolean).join(' ')}
                                            onClick={() => selectInvoiceLine(line, searchModalState, setSearchModalState)}
                                        >
                                            <td>
                                                <input type="checkbox"
                                                       checked={searchModalState.selectedLines.includes(line.id)}
                                                       onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                           e.target.checked ?
                                                               selectInvoiceLine(line, searchModalState, setSearchModalState)
                                                               :
                                                               unselectInvoiceLine(line, searchModalState, setSearchModalState)
                                                       }}
                                                />
                                            </td>
                                            <td>{line.lineNumber}</td>
                                            <td>
                                                <input type="text"
                                                       className="form-control form-control"
                                                       value={line.sfdcLineNumber}
                                                       onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                           const value = e.target.value;
                                                           setSearchModalState((state: SearchModalState) => ({
                                                               ...state,
                                                               lines: state.lines.map((l: InvoiceLine) => (
                                                                   (l.id === line.id) ? {
                                                                       ...line,
                                                                       sfdcLineNumber: value
                                                                   } : l
                                                               ))
                                                           }));
                                                       }}
                                                />
                                            </td>
                                            <td colSpan={line?.custom ? 4 : 1}>
                                                {line?.custom ?
                                                    <div style={{width: '100%'}}>
                                                    <ReactSelect
                                                        required={true}
                                                        onChange={(option) => {
                                                            handleMaterialChange(line, option?.value, setSearchModalState);
                                                        }}
                                                        options={customMaterialNumbers.filter(m => {
                                                            if (m.hasOwnProperty('filterBySalesOrg')) {
                                                                return m.filterBySalesOrg.includes(searchModalState?.selectedInvoice?.salesOrg);
                                                            }
                                                            return true;
                                                        }).map(m => ({
                                                            label: m.label,
                                                            value: m.value
                                                        }))}
                                                        defaultValue={null}
                                                        value={{label: line.materialNumber, value: line.materialNumber}}
                                                        placeholder=""
                                                    />
                                                    </div>
                                                    :
                                                    line.materialNumber
                                                }
                                            </td>
                                            {line?.custom !== true &&
                                                <>
                                                    <td>{line?.businessUnit}</td>
                                                    <td>
                                                        {!line?.custom &&
                                                            <ReactSelect
                                                                required={true}
                                                                onChange={(option) => {
                                                                    handleLineUnitOfMeasureChange(line, option?.value, setSearchModalState)
                                                                }}
                                                                options={validUnitOfMeasures.map(v => ({
                                                                    label: v,
                                                                    value: v
                                                                }))}
                                                                defaultValue={null}
                                                                value={{label: line.unitOfMeasure, value: line.unitOfMeasure}}
                                                                placeholder=""
                                                            />
                                                        }
                                                    </td>
                                                    <td>
                                                        {!line?.custom &&
                                                            <ReactSelect
                                                                required={true}
                                                                onChange={(option) => {
                                                                    handleLinePricingPerChange(line, option?.value, setSearchModalState);
                                                                }}
                                                                options={[1, 100, 1000].map(v => ({
                                                                    label: v,
                                                                    value: v
                                                                }))}
                                                                defaultValue={{label: 1, value: 1}}
                                                                value={{label: line.pricingPer, value: line.pricingPer}}
                                                                placeholder={''}
                                                            />
                                                        }
                                                    </td>
                                                </>
                                            }
                                            <td>
                                                {line?.custom ?
                                                    line.quantity
                                                    :
                                                    <input type="text"
                                                           className="form-control form-control"
                                                           value={line.quantity}
                                                           onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleLineQuantityChange(line, e.target.value, setSearchModalState)}
                                                           onBlur={(e: React.FocusEvent<HTMLInputElement>) => handleLineQuantityBlur(line, e.target.value, setSearchModalState)}
                                                    />
                                                }
                                            </td>
                                            <td>
                                                <input type="text"
                                                       className="form-control"
                                                       value={line.netPrice}
                                                       onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleLineNetPriceChange(line, e.target.value, setSearchModalState)}
                                                       onBlur={(e: React.FocusEvent<HTMLInputElement>) => handleLineNetPriceBlur(line, e.target.value, setSearchModalState)}
                                                />
                                            </td>
                                            <td>
                                                <input type="text"
                                                       className="form-control"
                                                       value={line.netRevenue}
                                                       onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleLineNetRevenueChange(line, e.target.value, setSearchModalState)}
                                                       onBlur={(e: React.FocusEvent<HTMLInputElement>) => handleLineNetRevenueBlur(line, e.target.value, setSearchModalState)}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            }

                            {customMaterialNumbers?.length > 0 && searchModalState.selectedInvoice &&
                                <button type="button"
                                        className="btn btn-sm btn-link"
                                        onClick={() => {

                                            // get max line on invoice
                                            let maxLineNumber = Math.max.apply(Math, searchModalState?.lines?.map(l => parseInt(l.lineNumber, 10)));

                                            // @ts-ignore
                                            setSearchModalState((state: SearchModalState) => ({
                                                ...state,
                                                lines: [
                                                    ...state.lines,
                                                    {
                                                        id: Math.max(...state.lines.map(o => parseInt(o.id, 10))) + 1,
                                                        businessUnit: '',
                                                        pricingPer: {text: 1, label: 1, value: 1},
                                                        quantity: '1',
                                                        netRevenue: '1',
                                                        netPrice: '1',
                                                        unitOfMeasure: 'EA',
                                                        lineNumber: String(maxLineNumber + 10).padStart(5, '0'),
                                                        custom: true
                                                    }
                                                ]
                                            }));
                                        }}
                                >Add Line</button>
                            }
                        </div>

                    </div>
                    <div className="modal-footer">
                        <button type="button"
                                className="btn btn-primary"
                                disabled={searchModalState.selectedLines.length === 0 || searchModalState.validationErrors.length > 0}
                                onClick={() => {
                                    const filterSelectedLines = (l: any) => {
                                        return searchModalState.selectedLines.includes(l.id);
                                    };

                                    // validate the SFDC Claim Line#
                                    if (formState?.fieldValues?.input_25) {
                                        if (requireSfdcLineNumber && searchModalState?.lines?.filter(filterSelectedLines)?.length !== searchModalState?.lines?.filter(filterSelectedLines).filter(l => l.sfdcLineNumber)?.length) {
                                            alert('One or more lines is missing a SFDC Case Line#');
                                            return;
                                        }
                                    }

                                    // NOTE - this doesn't really belong here
                                    // Validate that custom lines have line number and material number set
                                    const customLines = searchModalState?.lines?.filter(filterSelectedLines)?.filter((l: InvoiceLine) => l?.custom);
                                    if (customLines?.length > 0) {
                                        if (customLines?.filter(filterSelectedLines)?.filter((l: InvoiceLine) => l?.lineNumber)?.length !== customLines?.length) {
                                            alert('One or more selected lines is missing a line number.');
                                            return;
                                        }
                                        if (customLines?.filter(filterSelectedLines)?.filter((l: InvoiceLine) => l?.materialNumber)?.length !== customLines?.length) {
                                            alert('One or more selected lines is missing a material number.');
                                            return;
                                        }
                                    }

                                    if (validateAndSetDefaults(formState, form, searchModalState, setSearchModalState, workflowFieldMap)) {
                                        setSearchModalState((state: SearchModalState) => ({
                                            ...state,
                                            goForSave: true
                                        }));
                                    }
                                }}
                        >Add Selected Lines to Credit
                        </button>
                    </div>
                </Modal>
            }

            {searchModalState.validationErrors.length > 0 &&
                <Modal>
                    <div className="modal-header">
                        <h5 className="modal-title">Warnings {searchModalState.workingValidationErrorIndex + 1} of {searchModalState.validationErrors.length}</h5>
                        <button type="button"
                                className="close"
                                tabIndex={-1}
                                onClick={() => {
                                    setSearchModalState((state: SearchModalState) => ({
                                        ...state,
                                        validationErrors: []
                                    }))
                                }}
                        >×
                        </button>
                    </div>
                    <div className="modal-body">
                        {React.createElement(
                            getValidationErrorComponent(searchModalState.validationErrors[searchModalState.workingValidationErrorIndex]),
                            {
                                formState,
                                form,
                                searchModalState,
                                setSearchModalState,
                                workflowFieldMap
                            }
                        )}
                    </div>
                    <div className="modal-footer">
                        <button type="button"
                                className="btn btn-secondary mr-auto"
                                onClick={() => {
                                    setSearchModalState((state: SearchModalState) => ({
                                        ...state,
                                        validationErrors: []
                                    }))
                                }}
                        >Cancel and Continue Editing
                        </button>

                        {searchModalState.workingValidationErrorIndex < (searchModalState.validationErrors.length - 1) &&
                            <button type="button"
                                    className="btn btn-primary"
                                    disabled={!(
                                        ['keep', 'update'].includes(getSelectedValidationResolution(searchModalState))
                                    )}
                                    onClick={() => {
                                        setSearchModalState((state: SearchModalState) => ({
                                            ...state,
                                            workingValidationErrorIndex: state.workingValidationErrorIndex + 1
                                        }))
                                    }}
                            >Continue
                            </button>
                        }

                        {searchModalState.workingValidationErrorIndex === (searchModalState.validationErrors.length - 1) &&
                            <button type="button"
                                    className="btn btn-primary"
                                    disabled={!(
                                        ['keep', 'update'].includes(getSelectedValidationResolution(searchModalState))
                                    )}
                                    onClick={() => {
                                        // apply formUpdates based on answers
                                        let formStateUpdates = {};
                                        Object.keys(searchModalState.validationResolutions).forEach((prop: string) => {
                                            if (searchModalState.validationResolutions[prop] === 'update') {
                                                formStateUpdates = {
                                                    ...formStateUpdates,
                                                    ...getFormStateUpdatesForResolution(searchModalState, prop, workflowFieldMap)
                                                };
                                            }
                                        });

                                        setSearchModalState((state: SearchModalState) => ({
                                            ...state,
                                            formStateUpdates: {
                                                ...state.formStateUpdates,
                                                ...formStateUpdates
                                            },
                                            goForSave: true
                                        }));
                                    }}
                            >Add Lines
                            </button>
                        }
                    </div>
                </Modal>
            }
        </>
    );
};